import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const useBlogPosts = () => {
  const queryClient = useQueryClient();

  const { data: blogPosts, isLoading } = useQuery({
    queryKey: ['blog_posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const createBlogPost = useMutation({
    mutationFn: async (newBlogPost: any) => {
      const { data, error } = await supabase
        .from('blog_posts')
        .insert([newBlogPost])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog_posts'] });
      toast.success('Blog post created successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create blog post');
    },
  });

  const updateBlogPost = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      const { data, error } = await supabase
        .from('blog_posts')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog_posts'] });
      toast.success('Blog post updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update blog post');
    },
  });

  const deleteBlogPost = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog_posts'] });
      toast.success('Blog post deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete blog post');
    },
  });

  return {
    blogPosts,
    isLoading,
    createBlogPost,
    updateBlogPost,
    deleteBlogPost,
  };
};
